import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Check } from 'lucide-react';
import { fileVariantUrl, formatEuro, formatInteger, formatTime } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import {
  Button,
  Dialog,
  Eyebrow,
  ImageSlot,
  Pill,
  Select,
  TextField,
  Textarea,
  useToast,
} from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { releaseProperty, requestRelease } from '../api';
import { dateWithTime, parseDecimal } from '../format';
import type {
  ActivityEntry,
  PatchPropertyBody,
  ReleaseResponse,
  ReleaseState,
  WizardDocument,
} from '../types';
import { DeveloperLinkCard } from './DeveloperLinkCard';
import type { useChecklistActions } from './useChecklistActions';
import styles from './wizard.module.css';

type Actions = ReturnType<typeof useChecklistActions>;

const RATE_ERROR = 'Ohne Provisionssatz ist keine Freigabe möglich.';
const RATE_INVALID = 'Bitte einen Prozentsatz zwischen 0 und 100 angeben.';

const percent2 = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const percentInput = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 3 });

/** 6 → "6,00 %" (the commission rate is always shown with two decimals on the screens). */
export function ratePercent(pct: number): string {
  return `${percent2.format(pct)} %`;
}

function rateText(pct: number | null): string {
  return pct === null ? '' : percentInput.format(pct);
}

// --- main: Provisionssatz + Was noch fehlt / Voraussetzungen ---------------------------------------------

export interface ReleaseMainProps {
  doc: WizardDocument;
  actions: Actions;
  onPatch: (body: PatchPropertyBody) => void;
}

export function ReleaseMain({ doc, actions, onPatch }: ReleaseMainProps) {
  const { release } = doc;
  const [rate, setRate] = useState(() => rateText(release.ratePct));
  const [seeded, setSeeded] = useState(release.ratePct);
  const [invalid, setInvalid] = useState(false);
  const blocked = release.state === 'blocked';

  useEffect(() => {
    if (release.ratePct !== seeded) {
      setSeeded(release.ratePct);
      setRate(rateText(release.ratePct));
      setInvalid(false);
    }
  }, [release.ratePct, seeded]);

  function changeRate(text: string) {
    setRate(text);
    if (text.trim() === '') {
      setInvalid(false);
      onPatch({ commissionRatePct: null });
      return;
    }
    const value = parseDecimal(text);
    if (value === null || value < 0 || value > 100) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onPatch({ commissionRatePct: value });
  }

  const rateError = invalid ? RATE_INVALID : release.ratePct === null ? RATE_ERROR : undefined;
  const prerequisites = release.prerequisites;

  return (
    <>
      <section className={styles.card} aria-labelledby="wizard-rate">
        <h2 id="wizard-rate" className={styles.cardTitle}>
          Provisionssatz
        </h2>
        <p className={styles.cardText}>
          Gilt für alle Einheiten dieses Objekts. Pflichtfeld für die Vertriebsfreigabe.
        </p>
        <div className={styles.rateGrid}>
          <TextField
            label="Gesamtprovision"
            required
            inputMode="decimal"
            value={rate}
            error={rateError}
            readOnly={!release.canSetRate}
            trailing={<span className={styles.calcUnit}>%</span>}
            onChange={(event) => changeRate(event.target.value)}
          />
          <Select
            label="Bezugsgröße"
            value={release.base}
            options={[{ value: release.base, label: release.baseLabel }]}
            readOnly
            onChange={() => undefined}
          />
        </div>
        {release.exampleUnit && release.ratePct !== null ? (
          <div className={styles.exampleBox}>{release.exampleUnit.sentence}</div>
        ) : null}
      </section>

      {blocked ? (
        <section className={styles.card} aria-labelledby="wizard-missing">
          <h2 id="wizard-missing" className={styles.cardTitle}>
            Was noch fehlt
          </h2>
          <div className={styles.missingRows}>
            {release.missing.map((entry) => {
              const target = entry.id ? { id: entry.id, title: entry.title, documentTypeId: null } : null;
              const busy = target !== null && actions.busyId === target.id;
              return (
                <div key={`${entry.kind}-${entry.id ?? 'rate'}`} className={styles.missingRow}>
                  <span className={styles.missingDot} aria-hidden="true" />
                  <span className={styles.missingText}>
                    <span className={styles.missingTitle}>{entry.title}</span>
                    <span className={styles.missingSub}>{entry.subtitle}</span>
                  </span>
                  {target && entry.action === 'upload' ? (
                    <button
                      type="button"
                      className={clsx('pressable', styles.smallButton)}
                      disabled={actions.busyId !== null}
                      onClick={() =>
                        actions.startUpload({ ...target, documentTypeId: documentTypeOf(doc, target.id) })
                      }
                    >
                      Hochladen
                    </button>
                  ) : target && entry.action === 'request' ? (
                    <button
                      type="button"
                      className={clsx('pressable', styles.smallButton)}
                      disabled={actions.busyId !== null}
                      aria-busy={busy || undefined}
                      onClick={() => void actions.request(target)}
                    >
                      Anfordern
                    </button>
                  ) : target && entry.action === 'remind' ? (
                    <button
                      type="button"
                      className={clsx('pressable', styles.smallButton)}
                      disabled={actions.busyId !== null}
                      aria-busy={busy || undefined}
                      onClick={() => void actions.remind(target)}
                    >
                      Erinnern
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
          <input
            ref={actions.inputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.dwg,application/pdf,image/jpeg,image/png"
            hidden
            aria-hidden="true"
            tabIndex={-1}
            onChange={actions.onFileChange}
          />
        </section>
      ) : (
        <section className={styles.card} aria-labelledby="wizard-prerequisites">
          <h2 id="wizard-prerequisites" className={styles.cardTitle}>
            Voraussetzungen
          </h2>
          <div className={styles.missingRows}>
            <div className={styles.missingRow}>
              <span className={styles.missingCheck} aria-hidden="true">
                <Check size={11} strokeWidth={2.5} />
              </span>
              <span className={styles.missingText}>
                <span className={styles.missingTitle}>
                  Alle {prerequisites.checklist.required.total} Pflichtpunkte erfüllt
                </span>
                <span className={styles.missingSub}>
                  Checkliste · {prerequisites.checklist.all.done} von {prerequisites.checklist.all.total}{' '}
                  Punkten gesamt
                </span>
              </span>
            </div>
            <div className={styles.missingRow}>
              <span className={styles.missingCheck} aria-hidden="true">
                <Check size={11} strokeWidth={2.5} />
              </span>
              <span className={styles.missingText}>
                <span className={styles.missingTitle}>Provisionssatz gesetzt</span>
                <span className={styles.missingSub}>
                  {release.ratePct === null
                    ? '—'
                    : `${ratePercent(release.ratePct)} vom provisionsfähigen Kaufpreis`}
                </span>
              </span>
            </div>
            <div className={styles.missingRow}>
              {prerequisites.calculation ? (
                <span className={styles.missingCheck} aria-hidden="true">
                  <Check size={11} strokeWidth={2.5} />
                </span>
              ) : (
                <span className={styles.missingDot} aria-hidden="true" />
              )}
              <span className={styles.missingText}>
                <span className={styles.missingTitle}>Kalkulation hinterlegt</span>
                <span className={styles.missingSub}>
                  {prerequisites.calculation
                    ? `Version ${prerequisites.calculation.label} · Marge ${
                        prerequisites.calculation.marginCents === null
                          ? '—'
                          : formatEuro(prerequisites.calculation.marginCents)
                      }`
                    : 'Noch keine Kalkulation gespeichert'}
                </span>
              </span>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function documentTypeOf(doc: WizardDocument, itemId: string): string | null {
  for (const category of doc.checklist.categories) {
    const item = category.items.find((entry) => entry.id === itemId);
    if (item) return item.documentTypeId;
  }
  return null;
}

// --- sidebar: Vertriebsfreigabe by state ----------------------------------------------------------------

export interface ReleaseSidebarProps {
  doc: WizardDocument;
  /** The role label of the signed-in user („Freigabe bleibt gesperrt · Rolle: Vertrieb"). */
  roleLabel: string;
  onReleased: (response: ReleaseResponse) => void;
  onChanged: () => void;
}

function splitStateLabel(release: ReleaseState): { pill: string; rest: string | null } {
  const [pill, ...rest] = release.stateLabel.split(' · ');
  return { pill: pill ?? release.stateLabel, rest: rest.length > 0 ? rest.join(' · ') : null };
}

export function ReleaseSidebar({ doc, roleLabel, onReleased, onChanged }: ReleaseSidebarProps) {
  const { release, property } = doc;
  const { toast } = useToast();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recipientId, setRecipientId] = useState(release.recipients[0]?.id ?? '');
  const [message, setMessage] = useState('');
  const label = splitStateLabel(release);
  const pillVariant = release.state === 'blocked' ? 'warning' : 'success';

  async function doRelease() {
    setBusy(true);
    try {
      const response = await releaseProperty(property.id);
      setConfirm(false);
      onReleased(response);
    } catch (error) {
      toast({ message: errorMessage(error) });
      if (error instanceof ApiError && error.status === 409) {
        setConfirm(false);
        onChanged();
      }
    } finally {
      setBusy(false);
    }
  }

  async function doRequest() {
    if (!recipientId) {
      toast({ message: 'Bitte wählen Sie eine Person für die Freigabe.' });
      return;
    }
    setBusy(true);
    try {
      const response = await requestRelease(property.id, { recipientId, message: message.trim() || null });
      toast({ message: `Freigabe beantragt bei ${response.recipient.shortName}.` });
      setMessage('');
      onChanged();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }

  const units = release.prerequisites.units;
  const rateLine =
    release.ratePct === null ? '—' : `${ratePercent(release.ratePct)} vom provisionsfähigen KP`;

  return (
    <>
      <section className={styles.card} aria-labelledby="wizard-release">
        <Eyebrow id="wizard-release">Vertriebsfreigabe</Eyebrow>
        <div className={styles.pillRow}>
          <Pill variant={pillVariant}>{label.pill}</Pill>
          {label.rest ? <span className={styles.pillRowText}>{label.rest}</span> : null}
        </div>
        {release.state === 'releasable_by_others' ? (
          <>
            <div className={clsx(styles.sideText, styles.sideTextSmall)}>
              Sie haben kein Freigaberecht. Beantragen Sie die Freigabe, die Anfrage landet als Task bei der
              gewählten Person.
            </div>
            <div className={styles.sideStack}>
              <Select
                label="Freigabe durch"
                value={recipientId}
                placeholder="Person wählen"
                options={release.recipients.map((person) => ({
                  value: person.id,
                  label: `${person.shortName} · ${person.roleLabel}`,
                }))}
                disabled={release.pendingApproval !== null}
                onChange={(event) => setRecipientId(event.target.value)}
              />
              <Textarea
                label="Nachricht (optional)"
                placeholder="Hinweis für die Freigabe"
                rows={2}
                value={message}
                disabled={release.pendingApproval !== null}
                onChange={(event) => setMessage(event.target.value)}
              />
              <Button
                className={styles.primaryButton}
                loading={busy}
                disabled={release.pendingApproval !== null || release.recipients.length === 0}
                onClick={() => void doRequest()}
              >
                Freigabe beantragen
              </Button>
            </div>
            <div className={styles.statusLine} role="status">
              <span
                className={clsx(styles.statusDot, release.pendingApproval && styles.statusDotAccent)}
                aria-hidden="true"
              />
              {release.pendingApproval
                ? `Freigabe beantragt bei ${release.pendingApproval.recipientName} · ${dateWithTime(release.pendingApproval.requestedAt)}`
                : `Freigabe bleibt gesperrt · Rolle: ${roleLabel}`}
            </div>
          </>
        ) : (
          <>
            {release.state === 'blocked' ? (
              <div className={clsx(styles.sideText, styles.sideTextSmall, styles.sideTextBody)}>
                Freigeben können Org-Admins und Nutzer mit Freigaberecht, sobald alle Pflichtpunkte erfüllt
                sind.
              </div>
            ) : null}
            <div className={styles.sideStack}>
              <Button
                className={styles.primaryButton}
                disabled={!release.canRelease || release.state !== 'releasable'}
                onClick={() => setConfirm(true)}
              >
                Für den Vertrieb freigeben
              </Button>
            </div>
            {release.pendingApproval ? (
              <div className={styles.statusLine} role="status">
                <span className={clsx(styles.statusDot, styles.statusDotAccent)} aria-hidden="true" />
                Freigabe beantragt von {release.pendingApproval.recipientName} ·{' '}
                {dateWithTime(release.pendingApproval.requestedAt)}
              </div>
            ) : null}
          </>
        )}
      </section>
      <DeveloperLinkCard
        propertyId={property.id}
        link={doc.developerLink}
        variant="checklist"
        onChanged={onChanged}
      />

      <Dialog
        open={confirm}
        onClose={() => (busy ? undefined : setConfirm(false))}
        eyebrow="Vertriebsfreigabe"
        title={`${property.name} für den Vertrieb freigeben?`}
        subtitle="Das Objekt erscheint danach für alle Vertriebsnutzer in der Objektübersicht, die Einheiten sind reservierbar."
        width={520}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(false)} disabled={busy}>
              Abbrechen
            </Button>
            <Button loading={busy} onClick={() => void doRelease()}>
              Freigeben
            </Button>
          </>
        }
      >
        <div className={styles.confirmRows}>
          <div className={styles.confirmRow}>
            <span className={styles.confirmLabel}>Einheiten</span>
            <span className={styles.confirmValue}>
              {formatInteger(units.total)} ·{' '}
              {units.available === units.total ? 'alle frei' : `${formatInteger(units.available)} frei`}
            </span>
          </div>
          <div className={styles.confirmRow}>
            <span className={styles.confirmLabel}>Provisionssatz</span>
            <span className={styles.confirmValue}>{rateLine}</span>
          </div>
          <div className={styles.confirmRow}>
            <span className={styles.confirmLabel}>Status danach</span>
            <span className={styles.confirmValue}>Im Vertrieb</span>
          </div>
          <div className={styles.confirmRow}>
            <span className={styles.confirmLabel}>Offene Kann-Punkte</span>
            <span className={styles.confirmValue}>
              {formatInteger(release.prerequisites.openOptional)} · bleiben in der Checkliste
            </span>
          </div>
        </div>
      </Dialog>
    </>
  );
}

// --- released (09) -------------------------------------------------------------------------------------

export interface ReleasedMainProps {
  doc: WizardDocument;
  result: ReleaseResponse;
  onUpload: () => void;
}

export function ReleasedMain({ doc, result, onUpload }: ReleasedMainProps) {
  const property = result.property;
  const rate = property.fields.commissionRatePct;
  return (
    <>
      <section className={styles.successCard} aria-labelledby="wizard-released">
        <div className={styles.successImage}>
          <ImageSlot
            chrome="frame"
            src={property.heroFileId ? fileVariantUrl(property.heroFileId, 'medium') : undefined}
            alt={`Objektbild · ${property.name}`}
            placeholder={`Objektbild · ${property.name}`}
            height={220}
            radius="none"
          />
        </div>
        <div className={styles.successBody}>
          <Eyebrow>Freigegeben</Eyebrow>
          <h2 id="wizard-released" className={styles.successTitle}>
            {property.name} ist im Vertrieb.
          </h2>
          <p className={styles.successText}>
            Alle Vertriebsnutzer sehen das Objekt jetzt in der Übersicht. Einheiten lassen sich reservieren
            und Deals zuordnen.
          </p>
          <div className={styles.successMetrics}>
            <div>
              <Eyebrow>Einheiten</Eyebrow>
              <div className={styles.successMetricValue}>{formatInteger(result.nextSteps.units)}</div>
            </div>
            <div>
              <Eyebrow>Provision</Eyebrow>
              <div className={styles.successMetricValue}>{rate === null ? '—' : ratePercent(rate)}</div>
            </div>
            <div>
              <Eyebrow>Checkliste</Eyebrow>
              <div className={styles.successMetricValue}>
                {doc.checklist.progress.all.done} / {doc.checklist.progress.all.total}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className={styles.card} aria-labelledby="wizard-next">
        <h2 id="wizard-next" className={styles.cardTitle}>
          Nächste Schritte
        </h2>
        <div className={styles.nextRows}>
          <div className={styles.nextRow}>
            <span className={styles.nextText}>
              <span className={styles.nextTitle}>Einheiten prüfen</span>
              <span className={styles.nextSub}>{result.nextSteps.unitsLine}</span>
            </span>
            <Link to={`/properties/${property.id}`} className={clsx('pressable', styles.smallButton)}>
              Einheitenliste
            </Link>
          </div>
          <div className={styles.nextRow}>
            <span className={styles.nextText}>
              <span className={styles.nextTitle}>Exposé und Fotos ergänzen</span>
              <span className={styles.nextSub}>{result.nextSteps.optionalLine}</span>
            </span>
            <button type="button" className={clsx('pressable', styles.smallButton)} onClick={onUpload}>
              Hochladen
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

export function ReleasedSidebar({ protocol }: { protocol: ActivityEntry[] }) {
  return (
    <>
      <section className={styles.card} aria-labelledby="wizard-protocol">
        <Eyebrow id="wizard-protocol">Protokoll</Eyebrow>
        <div className={styles.protocol}>
          {protocol.length === 0 ? (
            <div className={styles.protocolRow}>
              <span className={styles.versionMeta}>Noch keine Einträge.</span>
            </div>
          ) : (
            protocol.map((entry, index) => (
              <div key={entry.id} className={styles.protocolRow}>
                <span className={clsx(styles.versionBadge, index === 0 && styles.versionBadgeCurrent)}>
                  {formatTime(entry.at)}
                </span>
                <span className={styles.versionText}>
                  <span className={styles.versionSummary}>{entry.title}</span>
                  <span className={styles.versionMeta}>{dateWithTime(entry.at).split(',')[0]}</span>
                </span>
              </div>
            ))
          )}
        </div>
      </section>
      <div className={styles.noteCard}>
        Spätere Änderungen am Provisionssatz gelten nur für neue Reservierungen, nicht rückwirkend.
      </div>
    </>
  );
}
